import { Switch, Route, useLocation, Redirect, Link } from "wouter";
import { lazy, Suspense } from "react";
import { queryClient } from "./lib/queryClient";
import { QueryClientProvider, useQuery } from "@tanstack/react-query";
import { HelmetProvider } from "react-helmet-async";
import { Toaster } from "@/components/ui/toaster";
import { TooltipProvider } from "@/components/ui/tooltip";
import { ThemeProvider } from "@/components/theme-provider";
import { ThemeToggle } from "@/components/theme-toggle";
import { SidebarProvider, SidebarTrigger } from "@/components/ui/sidebar";
import { AppSidebar } from "@/components/app-sidebar";
import { useAuth } from "@/hooks/use-auth";
import { usePreviewGate } from "@/hooks/use-preview-gate";
import { usePageView } from "@/hooks/use-page-view";
import { Skeleton } from "@/components/ui/skeleton";
import { ShortListProvider } from "@/contexts/short-list-context";
import { ShortListDrawer } from "@/components/short-list-drawer";
import { ErrorBoundary } from "@/components/error-boundary";
import { PageHelpProvider } from "@/hooks/use-page-help";
import { HelpToggle } from "@/components/help-toggle";
import { AdminRemindersBanner } from "@/components/admin-reminders-banner";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { Badge } from "@/components/ui/badge";
import type { AiAgent } from "@shared/schema";

import NotFound from "@/pages/not-found";
import PreviewGate from "@/pages/preview-gate";
import LandingPage from "@/pages/landing";
import LoginPage from "@/pages/login";
import SignupPage from "@/pages/signup";
import VerifyEmailPage from "@/pages/verify-email";
import InsightPage from "@/pages/insight";
import ArticlePage, { ArticleLegacyRedirectPage } from "@/pages/article";
import SocialWallPage from "@/pages/social-wall";
import PreviewPage from "@/pages/preview";
import CompanyHubPage from "@/pages/company-hub";
import PeopleHubPage from "@/pages/people-hub";
import ArticlesArchivePage from "@/pages/articles-archive";
import PublicPage from "@/pages/page";
import AboutPage from "@/pages/about";
import BrandPage from "@/pages/brand";
import ExplorePage from "@/pages/explore";
import SubscribePage from "@/pages/subscribe";
import RadarPage from "@/pages/radar";
import RadarLandingPage from "@/pages/radar-landing";
import RadarEvaluatePage from "@/pages/radar-evaluate";
import RadarResultsPage from "@/pages/radar-results";
import VideosPage from "@/pages/videos";
import FeaturedCompaniesPage from "@/pages/featured-companies";
import LegalHubPage from "@/pages/legal";
import LegalVersionPage from "@/pages/legal-version";
import MembershipPage from "@/pages/membership";
import MembershipApplyPage from "@/pages/membership-apply";
import { LegalAcceptanceGate } from "@/components/legal-acceptance-gate";
import ActionCardsPage from "@/pages/action-cards";
import ActionCardsDetailPage from "@/pages/action-cards-detail";
import GlossaryIndexPage from "@/pages/glossary";
import GlossaryTermPage from "@/pages/glossary-term";
import SetupPage from "@/pages/setup";
import MagazineListingPage from "@/pages/magazine";
import MagazineIssuePage from "@/pages/magazine-issue";
import StaffPortalPage from "@/pages/staff-portal";
import AccountPage from "@/pages/account";
import AccountAffiliationsPage from "@/pages/account/affiliations";
import AccountSettingsPage from "@/pages/account/settings";
import AccountEvaluationsPage from "@/pages/account/evaluations";
import UserDashboard from "@/pages/dashboard";
import UserProfile from "@/pages/dashboard/profile";
import UserAffiliations from "@/pages/dashboard/affiliations";
import UserClients from "@/pages/dashboard/clients";
import ContentStudio from "@/pages/dashboard/content";
import UserPressReleases from "@/pages/dashboard/press-releases";
import UserSubmissions from "@/pages/dashboard/submissions";
import { UserSidebar } from "@/components/user-sidebar";
import { DevImpersonationBanner } from "@/components/dev-role-switcher";
import { ConsentProvider } from "@/contexts/consent-context";
import { BrandProvider } from "@/contexts/brand-context";
import { CookieBanner } from "@/components/cookie-banner";
import { TrackingScriptInjector } from "@/components/tracking-script-injector";

const AdminDashboard = lazy(() => import("@/pages/admin/dashboard"));
const ArticleFormPage = lazy(() => import("@/pages/admin/article-form"));
const ArticlesListPage = lazy(() => import("@/pages/admin/articles-list"));
const CompaniesPage = lazy(() => import("@/pages/admin/companies"));
const CompanyFormPage = lazy(() => import("@/pages/admin/company-form"));
const PeoplePage = lazy(() => import("@/pages/admin/people"));
const PersonFormPage = lazy(() => import("@/pages/admin/person-form"));
const RelationshipsPage = lazy(() => import("@/pages/admin/relationships"));
const RelationshipTagsPage = lazy(() => import("@/pages/admin/relationship-tags"));
const AuditLogPage = lazy(() => import("@/pages/admin/audit-log"));
const ConventionsPage = lazy(() => import("@/pages/admin/conventions"));
const IngestionQueuePage = lazy(() => import("@/pages/admin/ingestion-queue"));
const IngestionWorkflowPage = lazy(() => import("@/pages/admin/ingestion-workflow"));
const ClassificationConfigPage = lazy(() => import("@/pages/admin/classification-config"));
const MediaGalleryPage = lazy(() => import("@/pages/admin/media-gallery"));
const UsersPage = lazy(() => import("@/pages/admin/users"));
const TeamPage = lazy(() => import("@/pages/admin/team"));
const SettingsPage = lazy(() => import("@/pages/admin/settings"));
const ProjectDocsPage = lazy(() => import("@/pages/admin/docs"));
const CategoriesPage = lazy(() => import("@/pages/admin/categories"));
const ImagePresetsPage = lazy(() => import("@/pages/admin/image-presets"));
const SpecialContentPage = lazy(() => import("@/pages/admin/special-content"));
const SpecialContentFormPage = lazy(() => import("@/pages/admin/special-content-form"));
const SpecialContentCategoriesPage = lazy(() => import("@/pages/admin/special-content-categories"));
const MeetingsPage = lazy(() => import("@/pages/admin/meetings"));
const MeetingFormPage = lazy(() => import("@/pages/admin/meeting-form"));
const SiteContentPage = lazy(() => import("@/pages/admin/site-content"));
const PageSectionsPage = lazy(() => import("@/pages/admin/page-sections"));
const PagesAdminPage = lazy(() => import("@/pages/admin/pages"));
const GlossaryPage = lazy(() => import("@/pages/admin/glossary"));
const PressReleaseGeneratorPage = lazy(() => import("@/pages/admin/press-release-generator"));
const ContentStylesPage = lazy(() => import("@/pages/admin/content-styles"));
const EditorialPhilosophyPage = lazy(() => import("@/pages/admin/editorial-philosophy"));
const ActionCardPacksPage = lazy(() => import("@/pages/admin/action-card-packs"));
const CtaHubPage = lazy(() => import("@/pages/admin/cta-hub"));
const CtaLibraryPage = lazy(() => import("@/pages/admin/cta-library"));
const FeaturesDirectoryPage = lazy(() => import("@/pages/admin/features-directory"));
const LaunchReadinessPage = lazy(() => import("@/pages/admin/launch-readiness"));
const SeoSubmissionsPage = lazy(() => import("@/pages/admin/seo-submissions"));
const RedirectsPage = lazy(() => import("@/pages/admin/redirects"));
const SeoInternalLinksPage = lazy(() => import("@/pages/admin/seo-internal-links"));
const SeoSearchPerformancePage = lazy(() => import("@/pages/admin/seo-search-performance"));
const SeoQuickStartPage = lazy(() => import("@/pages/admin/seo-quick-start"));
const AdminManualPage = lazy(() => import("@/pages/admin/manual"));
const HelpContentPage = lazy(() => import("@/pages/admin/help-content"));
const SiteMenuPage = lazy(() => import("@/pages/admin/site-menu"));
const AiEditorialPage = lazy(() => import("@/pages/admin/ai-editorial"));
const AiImageGalleryPage = lazy(() => import("@/pages/admin/ai-image-gallery"));
const WorkflowCriteriaPage = lazy(() => import("@/pages/admin/workflow-criteria"));
const TemplatesHubPage = lazy(() => import("@/pages/admin/templates-hub"));
const PromptsHubPage = lazy(() => import("@/pages/admin/prompts-hub"));
const MenuSettingsPage = lazy(() => import("@/pages/admin/menu-settings"));
const SystemHealthPage = lazy(() => import("@/pages/admin/system-health"));
const EditorialHubPage = lazy(() => import("@/pages/admin/editorial-hub"));
const ReviewHubPage = lazy(() => import("@/pages/admin/review-hub"));
const AiHubPage = lazy(() => import("@/pages/admin/ai-hub"));
const InsightsHubPage = lazy(() => import("@/pages/admin/insights-hub"));
const InsightTemplatesPage = lazy(() => import("@/pages/admin/insight-templates"));
const CampaignsPage = lazy(() => import("@/pages/admin/campaigns"));
const HouseAdvertsPage = lazy(() => import("@/pages/admin/house-adverts"));
const AnalyticsPage = lazy(() => import("@/pages/admin/analytics"));
const TrackedLinksPage = lazy(() => import("@/pages/admin/tracked-links"));
const AdvertisingPage = lazy(() => import("@/pages/admin/advertising"));
const AdminBrandsPage = lazy(() => import("@/pages/admin/brands"));
const MagazinesPage = lazy(() => import("@/pages/admin/magazines"));
const MagazineIssuesPage = lazy(() => import("@/pages/admin/magazine-issues"));
const MagazineFlatplanPage = lazy(() => import("@/pages/admin/magazine-flatplan"));
const MagazineTemplatesPage = lazy(() => import("@/pages/admin/magazine-templates"));
const MagazineStyleGuidePage = lazy(() => import("@/pages/admin/magazine-style-guide"));
const TemplateBuilderPage = lazy(() => import("@/pages/admin/template-builder"));
const BulkImportPage = lazy(() => import("@/pages/admin/bulk-import"));
const ArticleImportPage = lazy(() => import("@/pages/admin/article-import"));
const EventsPage = lazy(() => import("@/pages/admin/events"));
const EventFormPage = lazy(() => import("@/pages/admin/event-form"));
const SocialWallsPage = lazy(() => import("@/pages/admin/social-walls"));
const SocialWallFormPage = lazy(() => import("@/pages/admin/social-wall-form"));
const AdminLegalPage = lazy(() => import("@/pages/admin/legal"));
const TrackingScriptsPage = lazy(() => import("@/pages/admin/tracking-scripts"));
const MembershipsPage = lazy(() => import("@/pages/admin/memberships"));
const PipelinePage = lazy(() => import("@/pages/admin/pipeline"));
const PipelineInstancePage = lazy(() => import("@/pages/admin/pipeline-instance"));
const PipelineTemplatesPage = lazy(() => import("@/pages/admin/pipeline-templates"));
const SalesManualPage = lazy(() => import("@/pages/admin/sales-manual"));
const PartnershipsPage = lazy(() => import("@/pages/admin/partnerships"));
const PartnerLandingPage = lazy(() => import("@/pages/partner"));
const AdminRadarPage = lazy(() => import("@/pages/admin/radar"));
const AdminRadarDetailPage = lazy(() => import("@/pages/admin/radar-detail"));
const AdminRadarPreviewPage = lazy(() => import("@/pages/admin/radar-preview"));
const SupplierRadarsPage = lazy(() => import("@/pages/supplier/radars"));
const SupplierRadarDetailPage = lazy(() => import("@/pages/supplier/radar-detail"));

function AgentPresence({ pagePath }: { pagePath: string }) {
  const { data: agents = [] } = useQuery<AiAgent[]>({
    queryKey: ["/api/admin/ai-agents", "for-page", pagePath],
    queryFn: () =>
      fetch(`/api/admin/ai-agents/for-page?path=${encodeURIComponent(pagePath)}`, { credentials: "include" })
        .then(r => r.ok ? r.json() : []),
    staleTime: 60_000,
  });

  if (agents.length === 0) return null;

  const getInitials = (name: string) =>
    name.split(" ").map(w => w[0]).join("").toUpperCase().slice(0, 2);

  return (
    <div className="flex items-center gap-1" data-testid="agent-presence">
      {agents.map(agent => (
        <Popover key={agent.id}>
          <Tooltip>
            <TooltipTrigger asChild>
              <PopoverTrigger asChild>
                <button
                  className="rounded-full focus:outline-none focus-visible:ring-2 focus-visible:ring-primary"
                  data-testid={`agent-avatar-${agent.id}`}
                  aria-label={`${agent.name} — ${agent.role}`}
                >
                  <Avatar className="h-7 w-7 cursor-pointer ring-2 ring-background hover:ring-primary transition-all">
                    {agent.avatarUrl && <AvatarImage src={agent.avatarUrl} alt={agent.name} />}
                    <AvatarFallback className="text-[10px] font-semibold bg-primary/10 text-primary">
                      {getInitials(agent.name)}
                    </AvatarFallback>
                  </Avatar>
                </button>
              </PopoverTrigger>
            </TooltipTrigger>
            <TooltipContent side="bottom">
              <p className="font-semibold text-xs">{agent.name}</p>
              <p className="text-xs text-muted-foreground">{agent.role}</p>
            </TooltipContent>
          </Tooltip>
          <PopoverContent side="bottom" align="end" className="w-64 p-4">
            <div className="flex items-start gap-3">
              <Avatar className="h-10 w-10 shrink-0">
                {agent.avatarUrl && <AvatarImage src={agent.avatarUrl} alt={agent.name} />}
                <AvatarFallback className="text-sm font-semibold bg-primary/10 text-primary">
                  {getInitials(agent.name)}
                </AvatarFallback>
              </Avatar>
              <div className="space-y-1 min-w-0">
                <p className="font-semibold leading-tight">{agent.name}</p>
                <Badge variant="secondary" className="text-xs">{agent.role}</Badge>
                {agent.personaPrompt && (
                  <p className="text-xs text-muted-foreground line-clamp-3 mt-1">
                    {agent.personaPrompt}
                  </p>
                )}
              </div>
            </div>
            <div className="mt-3 pt-3 border-t">
              <Link
                href="/admin/ai-hub"
                className="text-xs text-primary hover:underline"
              >
                Manage agents →
              </Link>
            </div>
          </PopoverContent>
        </Popover>
      ))}
    </div>
  );
}

function AdminLayout({ children }: { children: React.ReactNode }) {
  const [location] = useLocation();
  const pageId = location.replace(/^\/admin\/?/, "").split("/")[0] || "admin";

  const style = {
    "--sidebar-width": "16rem",
    "--sidebar-width-icon": "3rem",
  };

  return (
    <PageHelpProvider pageId={pageId}>
      <SidebarProvider style={style as React.CSSProperties}>
        <div className="flex h-screen w-full">
          <AppSidebar />
          <div className="flex flex-1 flex-col overflow-hidden">
            {import.meta.env.DEV && <DevImpersonationBanner />}
            <header data-admin-header className="flex h-14 items-center justify-between gap-4 border-b bg-background px-4">
              <SidebarTrigger data-testid="button-sidebar-toggle" />
              <div className="flex items-center gap-2">
                <AgentPresence pagePath={location} />
                <div className="flex items-center gap-1">
                  <HelpToggle />
                  <ThemeToggle />
                </div>
              </div>
            </header>
            {location === "/admin" && <AdminRemindersBanner />}
            <main className="flex-1 overflow-auto p-6">
              {children}
            </main>
          </div>
        </div>
      </SidebarProvider>
    </PageHelpProvider>
  );
}

function UserDashboardLayout({ children }: { children: React.ReactNode }) {
  const style = {
    "--sidebar-width": "16rem",
    "--sidebar-width-icon": "3rem",
  };

  return (
    <SidebarProvider style={style as React.CSSProperties}>
      <div className="flex h-screen w-full">
        <UserSidebar />
        <div className="flex flex-1 flex-col overflow-hidden">
          {import.meta.env.DEV && <DevImpersonationBanner />}
          <header className="flex h-14 items-center justify-between gap-4 border-b bg-background px-4">
            <SidebarTrigger data-testid="button-user-sidebar-toggle" />
            <ThemeToggle />
          </header>
          <main className="flex-1 overflow-auto p-6">
            {children}
          </main>
        </div>
      </div>
    </SidebarProvider>
  );
}

function AdminRouter() {
  return (
    <AdminLayout>
      <Suspense fallback={<div className="flex h-full items-center justify-center"><Skeleton className="h-8 w-48" /></div>}>
        <Switch>
          <Route path="/admin" component={AdminDashboard} />
          <Route path="/admin/articles" component={ArticlesListPage} />
          <Route path="/admin/articles/:id" component={ArticleFormPage} />
          <Route path="/admin/companies" component={CompaniesPage} />
          <Route path="/admin/companies/new" component={CompanyFormPage} />
          <Route path="/admin/companies/:id" component={CompanyFormPage} />
          <Route path="/admin/bulk-import" component={BulkImportPage} />
          <Route path="/admin/article-import" component={ArticleImportPage} />
          <Route path="/admin/events" component={EventsPage} />
          <Route path="/admin/events/new" component={EventFormPage} />
          <Route path="/admin/events/:id" component={EventFormPage} />
          <Route path="/admin/social-walls" component={SocialWallsPage} />
          <Route path="/admin/social-walls/new" component={SocialWallFormPage} />
          <Route path="/admin/social-walls/:id/edit" component={SocialWallFormPage} />
          <Route path="/admin/people" component={PeoplePage} />
          <Route path="/admin/people/new" component={PersonFormPage} />
          <Route path="/admin/people/:id" component={PersonFormPage} />
          <Route path="/admin/relationships" component={RelationshipsPage} />
          <Route path="/admin/relationship-tags" component={RelationshipTagsPage} />
          <Route path="/admin/audit-log" component={AuditLogPage} />
          <Route path="/admin/conventions" component={ConventionsPage} />
          <Route path="/admin/categories" component={CategoriesPage} />
          <Route path="/admin/content-workflow" component={IngestionQueuePage} />
          <Route path="/admin/content-workflow/:batchId" component={IngestionWorkflowPage} />
          <Route path="/admin/classification-config" component={ClassificationConfigPage} />
          <Route path="/admin/media" component={MediaGalleryPage} />
          <Route path="/admin/image-presets" component={ImagePresetsPage} />
          <Route path="/admin/special-content" component={SpecialContentPage} />
          <Route path="/admin/special-content/categories" component={SpecialContentCategoriesPage} />
          <Route path="/admin/special-content/new" component={SpecialContentFormPage} />
          <Route path="/admin/special-content/:id" component={SpecialContentFormPage} />
          <Route path="/admin/meetings" component={MeetingsPage} />
          <Route path="/admin/meetings/new" component={MeetingFormPage} />
          <Route path="/admin/meetings/:id" component={MeetingFormPage} />
          <Route path="/admin/site-content" component={SiteContentPage} />
          <Route path="/admin/page-sections" component={PageSectionsPage} />
          <Route path="/admin/pages" component={PagesAdminPage} />
          <Route path="/admin/legal" component={AdminLegalPage} />
          <Route path="/admin/tracking-scripts" component={TrackingScriptsPage} />
          <Route path="/admin/team" component={TeamPage} />
          <Route path="/admin/users" component={UsersPage} />
          <Route path="/admin/settings" component={SettingsPage} />
          <Route path="/admin/docs" component={ProjectDocsPage} />
          <Route path="/admin/glossary" component={GlossaryPage} />
          <Route path="/admin/sponsors">{() => <Redirect to="/admin/campaigns" />}</Route>
          <Route path="/admin/sponsorships">{() => <Redirect to="/admin/campaigns" />}</Route>
          <Route path="/admin/press-release" component={PressReleaseGeneratorPage} />
          <Route path="/admin/content-styles" component={ContentStylesPage} />
          <Route path="/admin/editorial-philosophy" component={EditorialPhilosophyPage} />
          <Route path="/admin/action-card-packs" component={ActionCardPacksPage} />
          <Route path="/admin/cta-hub" component={CtaHubPage} />
          <Route path="/admin/cta-library" component={CtaLibraryPage} />
          <Route path="/admin/features" component={FeaturesDirectoryPage} />
          <Route path="/admin/seo-submissions" component={SeoSubmissionsPage} />
          <Route path="/admin/redirects" component={RedirectsPage} />
          <Route path="/admin/seo/internal-links" component={SeoInternalLinksPage} />
          <Route path="/admin/seo/search-performance" component={SeoSearchPerformancePage} />
          <Route path="/admin/seo/quick-start" component={SeoQuickStartPage} />
          <Route path="/admin/launch-readiness" component={LaunchReadinessPage} />
          <Route path="/admin/manual" component={AdminManualPage} />
          <Route path="/admin/help-content" component={HelpContentPage} />
          <Route path="/admin/site-menu" component={SiteMenuPage} />
          <Route path="/admin/ai-editorial" component={AiEditorialPage} />
          <Route path="/admin/ai-images" component={AiImageGalleryPage} />
          <Route path="/admin/workflow-criteria" component={WorkflowCriteriaPage} />
          <Route path="/admin/templates-hub" component={TemplatesHubPage} />
          <Route path="/admin/prompts-hub" component={PromptsHubPage} />
          <Route path="/admin/menu-settings" component={MenuSettingsPage} />
          <Route path="/admin/system-health" component={SystemHealthPage} />
          <Route path="/admin/editorial-hub" component={EditorialHubPage} />
          <Route path="/admin/review-hub" component={ReviewHubPage} />
          <Route path="/admin/ai-hub" component={AiHubPage} />
          <Route path="/admin/insights-hub" component={InsightsHubPage} />
          <Route path="/admin/insight-templates" component={InsightTemplatesPage} />
          <Route path="/admin/campaigns" component={CampaignsPage} />
          <Route path="/admin/house-adverts" component={HouseAdvertsPage} />
          <Route path="/admin/analytics" component={AnalyticsPage} />
          <Route path="/admin/tracked-links" component={TrackedLinksPage} />
          <Route path="/admin/advertising" component={AdvertisingPage} />
          <Route path="/admin/brands" component={AdminBrandsPage} />
          <Route path="/admin/magazines" component={MagazinesPage} />
          <Route path="/admin/magazines/templates" component={MagazineTemplatesPage} />
          <Route path="/admin/magazines/style-guide" component={MagazineStyleGuidePage} />
          <Route path="/admin/template-builder/:id" component={TemplateBuilderPage} />
          <Route path="/admin/magazines/:titleId" component={MagazineIssuesPage} />
          <Route path="/admin/magazines/:titleId/issues/:issueId/flatplan" component={MagazineFlatplanPage} />
          <Route path="/admin/memberships" component={MembershipsPage} />
          <Route path="/admin/pipeline" component={PipelinePage} />
          <Route path="/admin/pipeline/instances/:id" component={PipelineInstancePage} />
          <Route path="/admin/pipeline/templates" component={PipelineTemplatesPage} />
          <Route path="/admin/sales-manual" component={SalesManualPage} />
          <Route path="/admin/partnerships/:id" component={PartnershipsPage} />
          <Route path="/admin/partnerships" component={PartnershipsPage} />
          <Route path="/admin/radar" component={AdminRadarPage} />
          <Route path="/admin/radars/:id/preview" component={AdminRadarPreviewPage} />
          <Route path="/admin/radars/:id" component={AdminRadarDetailPage} />
          <Route component={NotFound} />
        </Switch>
      </Suspense>
    </AdminLayout>
  );
}

function UserDashboardRouter() {
  return (
    <UserDashboardLayout>
      <Switch>
        <Route path="/dashboard" component={UserDashboard} />
        <Route path="/dashboard/profile" component={UserProfile} />
        <Route path="/dashboard/affiliations" component={UserAffiliations} />
        <Route path="/dashboard/clients" component={UserClients} />
        <Route path="/dashboard/content" component={ContentStudio} />
        <Route path="/dashboard/press-releases" component={UserPressReleases} />
        <Route path="/dashboard/submissions" component={UserSubmissions} />
        <Route component={NotFound} />
      </Switch>
    </UserDashboardLayout>
  );
}

function SupplierLayout({ children }: { children: React.ReactNode }) {
  return (
    <div className="flex min-h-screen flex-col bg-background">
      {import.meta.env.DEV && <DevImpersonationBanner />}
      <header className="sticky top-0 z-40 border-b bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60">
        <div className="mx-auto flex h-14 max-w-5xl items-center justify-between gap-4 px-4">
          <div className="flex items-center gap-3">
            <Link href="/" className="font-bold text-base" data-testid="link-supplier-logo">
              FM World
            </Link>
            <span className="text-muted-foreground/50 text-sm">/</span>
            <span className="text-sm font-medium text-muted-foreground">Supplier Portal</span>
          </div>
          <div className="flex items-center gap-2">
            <ThemeToggle />
            <Link href="/supplier/radars" className="text-sm text-muted-foreground hover:text-foreground transition-colors" data-testid="link-my-radars">
              My Radars
            </Link>
          </div>
        </div>
      </header>
      <main className="flex-1 mx-auto w-full max-w-5xl px-4 py-6">
        {children}
      </main>
    </div>
  );
}

function SupplierRouter() {
  return (
    <SupplierLayout>
      <Suspense fallback={<div className="flex h-full items-center justify-center"><Skeleton className="h-8 w-48" /></div>}>
        <Switch>
          <Route path="/supplier/radars/:id" component={SupplierRadarDetailPage} />
          <Route path="/supplier/radars" component={SupplierRadarsPage} />
          <Route component={NotFound} />
        </Switch>
      </Suspense>
    </SupplierLayout>
  );
}

function ProtectedSupplierRoute() {
  const { isLoading, isAuthenticated } = useAuth();
  const [, setLocation] = useLocation();

  if (isLoading) {
    return (
      <div className="flex h-screen items-center justify-center">
        <div className="space-y-4 text-center">
          <Skeleton className="mx-auto h-12 w-12 rounded-full" />
          <Skeleton className="h-4 w-32" />
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    setLocation("/login");
    return null;
  }

  return <SupplierRouter />;
}

function ProtectedUserDashboardRoute() {
  const { isLoading, isAuthenticated } = useAuth();
  const [, setLocation] = useLocation();

  if (isLoading) {
    return (
      <div className="flex h-screen items-center justify-center">
        <div className="space-y-4 text-center">
          <Skeleton className="mx-auto h-12 w-12 rounded-full" />
          <Skeleton className="h-4 w-32" />
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    setLocation("/login");
    return null;
  }

  return <UserDashboardRouter />;
}

function ProtectedAdminRoute() {
  const { user, isLoading, isAuthenticated } = useAuth();
  const [location, setLocation] = useLocation();

  if (isLoading) {
    return (
      <div className="flex h-screen items-center justify-center">
        <div className="space-y-4 text-center">
          <Skeleton className="mx-auto h-12 w-12 rounded-full" />
          <Skeleton className="h-4 w-32" />
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    setLocation("/staff/portal");
    return null;
  }

  // Check if user has internal access (super_admin, admin, editor, or internal scope)
  const isInternalByRole = user && ["super_admin", "admin", "editor"].includes(user.role || "");
  const isInternalByScope = user?.roleScope === "internal";
  
  if (!isInternalByRole && !isInternalByScope) {
    // External user trying to access admin - redirect to user dashboard
    setLocation("/dashboard");
    return null;
  }

  return <AdminRouter />;
}

function PublicRouterContent() {
  return (
    <Switch>
      <Route path="/" component={LandingPage} />
      <Route path="/about" component={AboutPage} />
      <Route path="/login" component={LoginPage} />
      <Route path="/signup" component={SignupPage} />
      <Route path="/register" component={SignupPage} />
      <Route path="/verify-email" component={VerifyEmailPage} />
      <Route path="/setup" component={SetupPage} />
      <Route path="/staff/portal" component={StaffPortalPage} />
      <Route path="/account" component={AccountPage} />
      <Route path="/account/affiliations" component={AccountAffiliationsPage} />
      <Route path="/account/settings" component={AccountSettingsPage} />
      <Route path="/account/evaluations" component={AccountEvaluationsPage} />
      <Route path="/articles" component={ArticlesArchivePage} />
      <Route path="/articles/:year/:month/:slug" component={ArticlePage} />
      <Route path="/articles/:slug" component={ArticleLegacyRedirectPage} />
      <Route path="/companies/:slug" component={CompanyHubPage} />
      <Route path="/people/:slug" component={PeopleHubPage} />
      <Route path="/products" component={ActionCardsPage} />
      <Route path="/products/:slug" component={ActionCardsDetailPage} />
      <Route path="/glossary" component={GlossaryIndexPage} />
      <Route path="/glossary/:slug" component={GlossaryTermPage} />
      <Route path="/insights/:slug" component={InsightPage} />
      <Route path="/magazine" component={MagazineListingPage} />
      <Route path="/magazine/:slug/:issueNumber" component={MagazineIssuePage} />
      <Route path="/social/:slug" component={SocialWallPage} />
      <Route path="/preview/:token" component={PreviewPage} />
      <Route path="/page/:slug" component={PublicPage} />
      <Route path="/brand/:slug" component={BrandPage} />
      <Route path="/partner/:slug" component={PartnerLandingPage} />
      <Route path="/explore" component={ExplorePage} />
      <Route path="/subscribe" component={SubscribePage} />
      <Route path="/videos" component={VideosPage} />
      <Route path="/featured-companies" component={FeaturedCompaniesPage} />
      <Route path="/membership/apply" component={MembershipApplyPage} />
      <Route path="/membership" component={MembershipPage} />
      <Route path="/radar/:slug/results/:evaluationId" component={RadarResultsPage} />
      <Route path="/radar/:slug/evaluate" component={RadarEvaluatePage} />
      <Route path="/radar/:slug" component={RadarLandingPage} />
      <Route path="/radar" component={RadarPage} />
      <Route path="/legal" component={LegalHubPage} />
      <Route path="/legal/:slug/v/:version" component={LegalVersionPage} />
      <Route component={NotFound} />
    </Switch>
  );
}

function PublicRouter() {
  const [location] = useLocation();
  const { isLoading, needsGate } = usePreviewGate();
  usePageView();

  // Allow access to login/signup/register/setup/staff-portal/account/preview even when gate is enabled
  const isAuthPage = location === "/login" || location === "/signup" || location === "/register" || location === "/setup" || location === "/staff/portal" || location.startsWith("/account") || location.startsWith("/preview/");

  if (isLoading) {
    return (
      <div className="flex h-screen items-center justify-center">
        <div className="space-y-4 text-center">
          <Skeleton className="mx-auto h-12 w-12 rounded-full" />
          <Skeleton className="h-4 w-32" />
        </div>
      </div>
    );
  }

  if (needsGate && !isAuthPage) {
    return <PreviewGate onSuccess={() => window.location.reload()} />;
  }

  return <BrandProvider><PublicRouterContent /></BrandProvider>;
}

function Router() {
  const [location] = useLocation();
  
  // Check if we're in the admin section
  if (location.startsWith("/admin")) {
    return (
      <ErrorBoundary fallbackLabel="An error occurred in the admin panel. Check the browser console for details.">
        <ProtectedAdminRoute />
      </ErrorBoundary>
    );
  }

  // Check if we're in the supplier portal
  if (location.startsWith("/supplier")) {
    return (
      <ErrorBoundary fallbackLabel="An error occurred in the supplier portal.">
        <ProtectedSupplierRoute />
      </ErrorBoundary>
    );
  }

  // Check if we're in the user dashboard section
  if (location.startsWith("/dashboard")) {
    return (
      <ErrorBoundary fallbackLabel="An error occurred in the dashboard.">
        <ProtectedUserDashboardRoute />
      </ErrorBoundary>
    );
  }

  return (
    <ErrorBoundary fallbackLabel="An error occurred on this page.">
      <PublicRouter />
    </ErrorBoundary>
  );
}

function App() {
  return (
    <HelmetProvider>
      <QueryClientProvider client={queryClient}>
        <ThemeProvider defaultTheme="light" storageKey="deep-media-theme">
          <TooltipProvider>
            <ConsentProvider>
              <ShortListProvider>
                <Toaster />
                <TrackingScriptInjector />
                <LegalAcceptanceGate>
                  <Router />
                  <CookieBanner />
                </LegalAcceptanceGate>
                <ShortListDrawer />
              </ShortListProvider>
            </ConsentProvider>
          </TooltipProvider>
        </ThemeProvider>
      </QueryClientProvider>
    </HelmetProvider>
  );
}

export default App;
